import { FormEvent, ReactElement, startTransition, useRef, useState } from 'react';
import { useFormStatus } from 'react-dom';
import { useTranslation } from 'react-i18next';
import PersonIcon from '@mui/icons-material/Person';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  Link,
  FormGroup,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  FilledInput,
  FormHelperText,
  InputAdornment,
  IconButton,
  Typography,
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';

import { State } from '@/types';
import { LoginFields } from './LoginPage';
import { FrontPage } from './FrontPage';

import './LoginPage.css';

function Submit() {
  const { pending } = useFormStatus();
  const { t } = useTranslation();

  return (
    <Button
      className="login-page-submit-button"
      disabled={pending}
      type="submit"
      fullWidth
      variant="contained"
      data-qa-element-id="login-submit-button"
    >
      {pending ? t('login.inProgress') : t('login.signIn')}
    </Button>
  );
}

function LoginInputForm({
  state,
  formAction,
  rememberMePreviousValue,
  canBiometric,
  startBiometric,
  resetPw,
  isPhone,
}: {
  state: State<LoginFields>;
  formAction: (payload: FormData) => void;
  rememberMePreviousValue: boolean;
  canBiometric: boolean;
  startBiometric?: () => void;
  resetPw: () => void;
  isPhone: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const ref = useRef<HTMLFormElement | null>(null);

  function handleClickShowPassword() {
    setShowPassword(!showPassword);
  }

  // changed action to this
  // so we opt out of form reset
  // https://github.com/facebook/react/issues/29034
  function handleSubmit(event: FormEvent<HTMLFormElement> | undefined) {
    event?.preventDefault();
    const formData = new FormData(ref.current!);
    startTransition(() => formAction(formData));
  }

  return (
    <>
      {!isPhone && (
        <div className="login-page-right-title">
          <h1 className="login-page-right-title-signin">{t('login.signIn')}</h1>
          <span data-qa-element-id="for_registration_label">{t('registration.body')}</span>
        </div>
      )}
      <Box component="form" ref={ref} onSubmit={handleSubmit} noValidate>
        <FormControl
          variant="filled"
          margin="normal"
          fullWidth
          required
          autoFocus
          error={state.globalError !== undefined || state.errors.username !== undefined}
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: 'var(--mui-shape-borderRadius)',
            '& .MuiFormHelperText-root': {
              backgroundColor: '#fff',
              marginLeft: 0,
              marginRight: 0,
              paddingLeft: '14px',
              paddingRight: '14px',
              borderEndEndRadius: '3px',
              borderEndStartRadius: '3px',
              boxShadow: '0 2px 3px rgba(0, 0, 0, 0.1)',
            },
            '& .Mui-error::before': {
              borderWidth: 'thick',
            },
          }}
        >
          <InputLabel
            sx={{ color: 'white', fontWeight: 'bold' }}
            htmlFor="username"
            style={{ color: 'white' }}
            shrink={true}
          >
            {t('login.username')}
          </InputLabel>
          <FilledInput
            id="username"
            className="login-page-user-pass-textfield"
            name="username"
            type="username"
            autoComplete="username"
            sx={{ color: 'white' }}
            inputProps={{ autoCapitalize: 'none', 'data-qa-element-id': 'login-username-input' }}
            data-qa-element-id="login-username-div"
            endAdornment={
              <InputAdornment position="end" sx={{ padding: '8px' }}>
                <PersonIcon sx={{ color: 'white' }} fontSize="large" />
              </InputAdornment>
            }
          />
          <FormHelperText id="username-error-text" data-qa-element-id="login-username-error-text">
            {state.errors.username}
          </FormHelperText>
        </FormControl>

        <FormControl
          variant="filled"
          margin="normal"
          className="login-page-user-pass-shrinked"
          fullWidth
          required
          error={state.globalError !== undefined || state.errors.password !== undefined}
          sx={{
            backgroundColor: 'primary.main',
            borderRadius: 'var(--mui-shape-borderRadius)',
            '& .MuiFormHelperText-root': {
              backgroundColor: '#fff',
              marginLeft: 0,
              marginRight: 0,
              paddingLeft: '14px',
              paddingRight: '14px',
              borderEndEndRadius: '3px',
              borderEndStartRadius: '3px',
              boxShadow: '0 2px 3px rgba(0, 0, 0, 0.1)',
            },
            '& .Mui-error::before': {
              borderWidth: 'thick',
            },
          }}
        >
          <InputLabel
            sx={{ color: 'white', fontWeight: 'bold' }}
            htmlFor="password"
            style={{ color: 'white' }}
            shrink={true}
          >
            {t('login.password')}
          </InputLabel>
          <FilledInput
            id="password"
            className="login-page-user-pass-textfield"
            name="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            sx={{ color: 'white' }}
            inputProps={{ 'data-qa-element-id': 'login-password-input' }}
            data-qa-element-id="login-password-div"
            endAdornment={
              <InputAdornment position="end">
                <IconButton onClick={handleClickShowPassword}>
                  {showPassword ? (
                    <Visibility sx={{ color: 'white' }} fontSize="large" />
                  ) : (
                    <VisibilityOff sx={{ color: 'white' }} fontSize="large" />
                  )}
                </IconButton>
              </InputAdornment>
            }
          />
          <FormHelperText id="password-error-text" data-qa-element-id="login-password-error-text">
            {state.errors.password}
          </FormHelperText>
        </FormControl>

        {state.globalError !== undefined && (
          <div className="login-page-user-passwd-error">
            <div className="login-page-error-title">{state.globalError}!</div>
          </div>
        )}

        <Box className="login-page-store-password-box" sx={{ justifyContent: 'space-between', width: '100%' }}>
          <FormGroup>
            <FormControlLabel
              control={
                <Checkbox
                  name="rememberMe"
                  defaultChecked={rememberMePreviousValue}
                  slotProps={{
                    input: {
                      // @ts-expect-error this works but gives error :(
                      'data-qa-element-id': 'remember-me-checkbox',
                    },
                  }}
                />
              }
              label={<Typography data-qa-element-id="login-remember-me-label">{t('login.rememberMe')}</Typography>}
            />
          </FormGroup>
          {canBiometric && (
            <IconButton size="large" onClick={startBiometric}>
              <FingerprintIcon fontSize="large" />
            </IconButton>
          )}
        </Box>

        <Submit />

        <Box className="login-page-forgot-password" mt={2} mb={2}>
          <Grid container direction="row" justifyContent="right" alignItems="center">
            <Grid item xs={7}>
              <Link
                component="button"
                onClick={resetPw}
                underline="none"
                data-qa-element-id="login-forgot-password-button"
              >
                {t('login.forgotPassword')}
              </Link>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </>
  );
}

export function LoginForm({
  isPhone,
  state,
  formAction,
  rememberMePreviousValue,
  canBiometric,
  startBiometric,
  resetPw,
}: {
  isPhone: boolean;
  state: State<LoginFields>;
  formAction: (payload: FormData) => void;
  rememberMePreviousValue: boolean;
  canBiometric: boolean;
  startBiometric?: () => void;
  resetPw: () => void;
}) {
  return (
    <FrontPage isPhone={isPhone}>
      <LoginInputForm
        rememberMePreviousValue={rememberMePreviousValue}
        state={state}
        formAction={formAction}
        canBiometric={canBiometric}
        startBiometric={canBiometric ? startBiometric : undefined}
        resetPw={resetPw}
        isPhone={isPhone}
      />
    </FrontPage>
  );
}
